// Flow Power Happy Hour plan settings shared by registration, onboarding,
// site edit, and the battery plan chart.
//
// Stored in the site's price_provider_credentials in $/kWh and "HH:MM"
// (site-local) — mirror of shared/adapters/price/flowpower_happy_hour.py.
// The form works in c/kWh because that's how Flow Power quotes the plan.

export interface FlowPowerWindowOption {
  start: string; // "HH:MM"
  end: string; // "HH:MM"
  label: string;
}

export const FLOWPOWER_WINDOW_OPTIONS: FlowPowerWindowOption[] = [
  { start: '17:30', end: '21:30', label: '5:30pm – 9:30pm (current plan)' },
  { start: '17:30', end: '19:30', label: '5:30pm – 7:30pm (plan before July 2026)' },
];

export const FLOWPOWER_DEFAULT_WINDOW = FLOWPOWER_WINDOW_OPTIONS[0];

// Premium Happy Hour rate per state (c/kWh), standard sign-up. Keep in sync
// with HAPPY_HOUR_RATES in the backend provider.
export const FLOWPOWER_STATE_RATES_CENTS: Record<string, number> = {
  NSW: 35,
  SA: 35,
  QLD: 35,
  VIC: 30,
};
export const FLOWPOWER_SOLAR_CHOICE_BONUS_CENTS = 5;
export const FLOWPOWER_AFTER_CAP_CENTS = 10;
export const FLOWPOWER_DAILY_CAP_KWH = 15;
export const FLOWPOWER_DEFAULT_USAGE_CENTS = 35;

export interface FlowPowerForm {
  solarChoice: boolean;
  usageRateCents: string; // blank = backend default
  window: string; // "HH:MM-HH:MM"
  rateOverrideCents: string; // blank = state default (+ Solar Choice)
}

export const defaultFlowPowerForm: FlowPowerForm = {
  solarChoice: false,
  usageRateCents: '',
  window: `${FLOWPOWER_DEFAULT_WINDOW.start}-${FLOWPOWER_DEFAULT_WINDOW.end}`,
  rateOverrideCents: '',
};

/** "NSW1" / "nsw" -> "NSW" (ACT rides NSW). */
export function regionToState(region: string | null | undefined): string {
  const s = (region || '').toUpperCase().replace(/[0-9]+$/, '');
  return s === 'ACT' ? 'NSW' : s;
}

function centsOrNull(value: string): number | null {
  const n = parseFloat(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** The premium rate (c/kWh) the site will be planned on. */
export function happyHourRateCents(form: FlowPowerForm, region: string | null | undefined): number {
  const override = centsOrNull(form.rateOverrideCents);
  if (override !== null) return override;
  const base = FLOWPOWER_STATE_RATES_CENTS[regionToState(region)] ?? FLOWPOWER_STATE_RATES_CENTS.NSW;
  return base + (form.solarChoice ? FLOWPOWER_SOLAR_CHOICE_BONUS_CENTS : 0);
}

/** Form -> price_provider_credentials ($/kWh). */
export function flowPowerCredentials(
  form: FlowPowerForm,
  region: string | null | undefined,
): Record<string, unknown> {
  const [start, end] = form.window.split('-');
  const usage = centsOrNull(form.usageRateCents);
  const override = centsOrNull(form.rateOverrideCents);
  const state = regionToState(region);
  return {
    solar_choice: form.solarChoice,
    window_start: start,
    window_end: end,
    ...(state ? { state } : {}),
    ...(usage !== null ? { import_rate: +(usage / 100).toFixed(5) } : {}),
    ...(override !== null ? { happy_hour_rate: +(override / 100).toFixed(5) } : {}),
  };
}

/** Stored price_provider_credentials -> form. */
export function flowPowerFormFromCredentials(
  creds: Record<string, unknown> | null | undefined,
): FlowPowerForm {
  const c = creds || {};
  const cents = (v: unknown) =>
    typeof v === 'number' && Number.isFinite(v) ? String(+(v * 100).toFixed(2)) : '';
  const start = typeof c.window_start === 'string' ? c.window_start : FLOWPOWER_DEFAULT_WINDOW.start;
  const end = typeof c.window_end === 'string' ? c.window_end : FLOWPOWER_DEFAULT_WINDOW.end;
  return {
    solarChoice: c.solar_choice === true,
    usageRateCents: cents(c.import_rate),
    window: `${start}-${end}`,
    rateOverrideCents: cents(c.happy_hour_rate),
  };
}

function hhmmToHours(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const minutes = Number(m[1]) * 60 + Number(m[2]);
  return minutes >= 0 && minutes <= 24 * 60 ? minutes / 60 : null;
}

/** The site's Happy Hour window in local hours (plan default when unset/invalid). */
export function resolveHappyHourWindow(
  creds: Record<string, unknown> | null | undefined,
): { startHour: number; endHour: number } {
  const start = hhmmToHours(typeof creds?.window_start === 'string' ? creds.window_start : '');
  const end = hhmmToHours(typeof creds?.window_end === 'string' ? creds.window_end : '');
  const d = FLOWPOWER_DEFAULT_WINDOW;
  return {
    startHour: start ?? hhmmToHours(d.start)!,
    endHour: end !== null && (start === null || end > start) ? end : hhmmToHours(d.end)!,
  };
}
